import { createFeatureSelector, createSelector } from '@ngrx/store';
import AnalysisHelper from 'projects/dop-ordering/src/lib/features/dop-analysis/dop-analysis.helper';
import { MemberType, SalesCatalogDisplayData, SalesCatalogueItem, ScTreeModel } from 'src/app/shared/models/sales-catalogue.model';
import { MetaDataState, SalesCatalogueData, StoreSalesCatalogue } from '../../store';

const getOrderFeatureState = createFeatureSelector<MetaDataState>('metaData');

export const getSalesCatalogDisplayData: any = createSelector(
  getOrderFeatureState,
  (state, prop) => getDisplayData(state.salesCatalogueData, prop.useProductMatrix)
);

export const getSalesCatalogueMrtData: any = createSelector(
  getOrderFeatureState,
  (state, props) => {
    let items: any[] = [];
    const sc = state.salesCatalogueData.salesCatalogues.filter(x => x.code === props.analysis.invoiceableItemParentCode).shift();
    if (props.analysis.preparationTests) {
      items = getPreparationTests(sc, props.analysis.preparationTests);
    }
    return items;
  });

export const getSalesCatalogCodes: any = createSelector(
  getOrderFeatureState,
  state => state.salesCatalogueData.salesCatalogues.map(x => x.code)
);

export const getPreparationTestData: any = createSelector
  (getOrderFeatureState, (state, prop) => getPreparationTests(
    state.salesCatalogueData.salesCatalogues.filter(x => x.code === prop.scCode).shift(),
    prop.testCodes));

export const getSalesCatalogApplicableMembers: any = createSelector(
  getOrderFeatureState,
  state => state.salesCatalogueData.applicableMembers
);

function getDisplayData(salesCatalogueData: SalesCatalogueData, useProductMatrix: boolean): SalesCatalogDisplayData[] {
  return salesCatalogueData.salesCatalogues
    .map(x => ({
      code: x.code,
      name: x.name,
      currencyCode: x.currencyCode,
      filteredMembers: convertToTreeModel(x, salesCatalogueData, useProductMatrix)
    }) as SalesCatalogDisplayData)
    .filter(sc => sc.filteredMembers?.length > 0);
}

function getPreparationTests(salesCatalogue: StoreSalesCatalogue, preparationTests: string[]) {
  const items: any[] = [];
  preparationTests.forEach(testCode => {
    const filteredKey = salesCatalogue.memberKeys.find(key => key.indexOf(testCode) !== -1);
    if (filteredKey) {
      const item = salesCatalogue.memberDictionary[filteredKey];
      if (item.code === testCode) {
      items.push(item);
      }  else {
        const i = item.tests?.find(t => t.code === testCode);
        if (i !== undefined) {
        items.push(i);
        }
      }
    }
  });
  return items;
}

function convertToTreeModel(salesCatalogue: StoreSalesCatalogue, salesCatalogueData: SalesCatalogueData, useProductMatrix: boolean)
  : ScTreeModel[] {
  const result: ScTreeModel[] = [];
  salesCatalogue.filteredMembers?.forEach(member => {
    if (!salesCatalogueData.excludeMembers.has(member.code)
      && (useProductMatrix ? salesCatalogueData.applicableMembers.has(member.code) : true)) {
      const model = new ScTreeModel();
      model.key = salesCatalogue.code + '|' + member.code + '|' + member.type;
      model.data.code = member.code;
      model.data.name = member.name;
      model.data.type = member.type;
      model.data.priceValue = member.price;
      model.data.price = AnalysisHelper.formatCurrencyValue(member?.price, salesCatalogue.currencyCode + ' ');
      model.parentCode = salesCatalogue.code;
      model.data.preparationTests = member.preparationTests;
      model.children = [];
      if (member.type === MemberType.TestBundle) {
        let filterMember = member.tests;
        // Test bundle member filtration
        if (salesCatalogueData.searchText) {
          filterMember = (member.code + member.name).toUpperCase().indexOf(salesCatalogueData.searchText) !== -1 ?
            member.tests :
            member.tests?.filter(x => (x.code + x.name).toUpperCase().indexOf(salesCatalogueData.searchText) !== -1);
        }
        if (salesCatalogueData.applicableMembers?.size > 0) {
          filterMember = filterMember?.filter(x => salesCatalogueData.applicableMembers.has(x.code));
        }
        model.children = filterMember.map(t =>
          ({
            data: { code: t.code, name: t.name, type: t.type, priceValue: t.price,
              price: AnalysisHelper.formatCurrencyValue(t.price, salesCatalogue.currencyCode + ' ') },
            children: [],
            parentCode: member.code,
            key: model.key + '|' + t.code
          }) as ScTreeModel) ?? [];
        if (filterMember?.length < member.tests?.length) {
          model.expanded = true;
        }
      }
      if (member.type === MemberType.TestBundle) {
        if (model.children.length > 0) {
          result.push(model);
        }
      } else {
        result.push(model);
      }
    }
  });
  return result;
}
